import { Injectable } from '@angular/core';

import { io, Socket } from 'socket.io-client';
import { Observable, switchMap } from 'rxjs';
import { AuthService, TokenService } from '@tablesready/frontend-auth';
import { LoggerService } from '@tablesready/frontend-logger';
import { BaseSocketService } from '@tablesready/frontend-store';
import { getBackEndInstance } from '@tablesready/frontend-get-backend';

declare const process: {
  env: {
    LOCAL_IP: string;
    PORT: string;
    NODE_ENV: string;
    NATIVE_APP_HOST_URL: string;
    SOCKET_PORT: string;
  };
};

@Injectable({ providedIn: 'root' })
export class SocketsService extends BaseSocketService {
  constructor(
    private readonly authService: AuthService,
    private readonly tokenService: TokenService,
    loggerService: LoggerService,
  ) {
    super(loggerService);
  }

  getBackendUrl(): string {
    if (process.env.SOCKET_PORT) {
      console.log('testing sockets through proxy');
      return `http://${process.env.LOCAL_IP}:${process.env.SOCKET_PORT}`;
    }

    if (process.env.NODE_ENV === 'development' && process.env.LOCAL_IP) {
      return `http://${process.env.LOCAL_IP}:${process.env.PORT}`;
    }

    return getBackEndInstance();
  }

  protected createSocket(): Observable<Socket> {
    // getAnonToken() fetches a fresh anonymous token over HTTP when the cached one is missing or
    // expired — so a rebuild (reconnectIfNeeded after a dead socket) always dials with usable auth.
    return this.authService.getAnonToken().pipe(
      switchMap((token) => {
        return new Observable<Socket>((observer) => {
          const socket = io(this.getBackendUrl(), {
            transports: ['websocket'],
            forceNew: true,
            reconnection: true,
            reconnectionDelay: 500,
            reconnectionAttempts: 10,
            // Callback form is re-evaluated on every (re)connection attempt: prefer the freshest
            // cached anon token over the one captured at creation, which can be expired after a
            // long-open page (a rejected token dead-ends the socket until the health-check
            // rebuild fetches a new one).
            auth: (callback) => callback({ token: `Bearer ${this.tokenService.getAnonToken() ?? token}` }),
          });

          this.registerLifecycleHandlers(socket, observer);

          return this.socketTeardown(socket);
        });
      })
    );
  }
}
